/**
 * Routes server errors from the QueryClient cache callbacks to the right UI.
 *
 * The axios interceptor cannot do this — it sees an HTTP status and nothing
 * else. The QueryClient layer additionally knows whether the request was a
 * mutation or a query, and whether a query has ever succeeded. That is enough
 * to split errors accurately, with no per-call-site annotation:
 *
 *   - mutation 5xx / transport     → the band of the dialog the action ran in
 *                                    while it is open (#1174), else FeedbackZone
 *                                    (a failed action never breaks the screen);
 *                                    a caller-mode mutation (CALLER_REPORTS):
 *                                    nothing here, the awaiting dialog child
 *                                    presents it (#1174-d)
 *   - query 5xx, never succeeded   → FailureScreen (nothing to render)
 *   - query 5xx, has prior data    → FeedbackZone (stale-but-valid still shows)
 *   - query with `meta.failureShownIn: 'region'` → neither (still reported):
 *                                    the region shows its own failed state
 *
 * Transport failures (no HTTP response: offline, DNS, timeout) are NEVER routed
 * to the failure screen and never reported. The connectivity controller pauses
 * queries and shows an inline "unreachable" state instead (#239).
 *
 * Only 5xx is surfaced globally — 4xx are the component's own concern (form
 * validation, etc.), and transport failures are the connectivity layer's concern.
 */
import axios, { type AxiosError } from 'axios';
import type { Mutation, Query } from '@tanstack/react-query';
import {
  wireQueryErrorRouting, PAGE_OWNS_FAILURE_META, FAILURE_SHOWN_IN_META, REPORT_KNOWN_REFUSAL_META,
} from '@/api/queryClient';
import { buildErrorReport } from '@/lib/buildErrorReport';
import { extractApiMessage } from '@/lib/apiError';
import { reportClientError } from '@/api/clientErrors';
import { classifyFailure, isTransportFailure } from '@/api/failureClass';
import { identifyRefusal, isViewRoute } from '@/api/knownRefusals';
import { traceIdFromError, stampTraceId } from '@/api/errorTrace';
import { BreakdownUnavailableError } from '@/api/breakdownUnavailable';
import { formMessages } from '@/constants';
import { SERVER_FAILURE_MESSAGE } from '@/constants/alertMessages';
import { CALLER_REPORTS } from '@/api/feedbackLayerMeta';
import { useFeedbackStore } from '@/store/useFeedbackStore';
import { useFailureStore, type FailureDetails } from '@/store/useFailureStore';

/** #1185: the one server-failure sentence (extractApiMessage already returns it for any 5xx). */
const FALLBACK_MESSAGE = SERVER_FAILURE_MESSAGE;

/** A 5xx server error. Transport failures (no HTTP response) are handled by the
 *  connectivity layer (#239) and are excluded: they never show the failure screen
 *  and never push a banner through this module. Drives PRESENTATION (failure
 *  screen vs. banner); telemetry uses `shouldReportError` below, which is broader
 *  (it covers the whole silent class this audit found). */
export function isServerFailure(error: unknown): error is AxiosError {
  return classifyFailure(error) === 'server';
}

/**
 * Should this failure raise a telemetry alert? The audit's core rule: alert on
 * the whole "we don't understand what happened" class, so nothing lands the
 * user an error-ish screen with no trace. Two arms:
 *
 *  - HTTP arm: status >= 400, EXCEPT the expected/handled ones — 401 (redirect),
 *    412 (context modal), 422 (already alerted server-side), 404 (resource absent
 *    or not visible in the current context), and any 4xx on the consumer /view
 *    routes (the viewer's own access-denied / invalid-link surface). A 404 is a
 *    normal "not found" outcome the component surfaces inline (dead link, deleted
 *    entity, or a stale query refetching after an org/context switch scopes the
 *    entity out) — never a client crash worth a telemetry alert. The view-route
 *    suppression is capped at 4xx (E1): a view-route 5xx is a server fault that
 *    used to be swallowed (failure screen, NO telemetry) and now reports.
 *  - No-status arm: the circuit-breaker cancel and any NON-Axios throw escaping
 *    a queryFn/mutationFn — `ResponseShapeError` (schema drift on a 200),
 *    `HtmlResponseError`, logic errors. This arm is the one that catches the
 *    /packets incident; a status-only rule would miss it.
 *
 * Transport failures (no HTTP response) return **false** (#239): the
 * connectivity layer handles them with an inline retry UI, not a crash alert.
 *
 * This telemetry gate is SEPARATE from consume-write alerting: a failed consume
 * PUT is always alerted at the interceptor (see client.ts), and the known-refusal
 * registry consulted below NEVER applies to it. This gate only governs the
 * QueryClient cache-callback (GET/query & mutation) telemetry.
 *
 * Genuine cancellations (abort on unmount / key change) return false.
 *
 * `options.reportKnownRefusal` (#1685): the surface renders a known refusal as a
 * failure, so the registry's exemption does not apply. Every other exemption holds.
 */
export function shouldReportError(error: unknown, options?: { reportKnownRefusal?: boolean }): boolean {
  // A 200 with no breakdown: the server already alerted (vingapi#2110), the
  // client retries it quietly and shows the panel's failed state (Q-506 card 1542 b).
  if (error instanceof BreakdownUnavailableError) return false;

  const cls = classifyFailure(error);

  // Transport failures are the connectivity layer's concern, not telemetry (#239).
  if (cls === 'transport_timeout' || cls === 'transport_network') return false;

  // Genuine cancellation — benign.
  if (cls === 'cancel') return false;

  // Circuit-breaker cancel and non-axios throws land in 'other' — report them.
  if (cls === 'other') return true;

  // Server 5xx — always report.
  if (cls === 'server') return true;

  // Client 4xx — report selectively.
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 401 || status === 412) return false; // handled UX flows
    if (status === 422) return false; // already alerted by the backend
    if (status === 404) return false;
    // E1: expected viewer 4xx (expired/invalid share link, access denied, not
    // enrolled) are shown inline and must not alert — but capped at 4xx so a
    // view-route 5xx/network falls through to report (it used to be swallowed).
    if (status !== undefined && status >= 400 && status < 500 && isViewRoute(error.config?.url)) return false;
    // #100: an ADMIN-FORM refusal the API issues deliberately and this client
    // renders as an inline field error is form validation, not a crash.
    if (identifyRefusal(error) && !options?.reportKnownRefusal) return false;
    return status !== undefined && status >= 400;
  }

  return false;
}

function buildFailure(error: AxiosError, traceId: string): FailureDetails {
  // `buildFailure` is only reachable after `isServerFailure` (status >= 500),
  // so `status` is always defined here.
  const status = error.response!.status;

  const method = (error.config?.method ?? 'GET').toUpperCase();
  const requestUrl = error.config?.url ? `${method} ${error.config.url}` : undefined;

  const data = error.response?.data;
  const responseBody =
    typeof data === 'string'
      ? data
      : data != null
        ? JSON.stringify(data, null, 2)
        : undefined;

  const report = buildErrorReport({
    title: `Server Error (${status})`,
    message: extractApiMessage(error, SERVER_FAILURE_MESSAGE),
    source: 'server',
    status,
    requestUrl,
    responseBody,
    traceId,
  });

  return { traceId, report };
}

/**
 * Resolve the failure's trace id ONCE, report it if it's alert-worthy, and
 * stamp it on the error so the inline UI can show the same id. Returns the id so
 * the presentation path (FailureScreen) reuses it — one id across telemetry,
 * screen, and screenshot. This is the fix for the audit's core gap: the cache
 * callbacks fire for 100% of query/mutation failures but never reached the
 * telemetry sink for anything but a (screen-breaking) 5xx.
 */
function instrument(
  error: unknown,
  source: 'query' | 'mutation',
  options?: { reportKnownRefusal?: boolean },
): string {
  const traceId = traceIdFromError(error);
  stampTraceId(error, traceId);
  if (shouldReportError(error, options)) {
    reportClientError({
      error,
      source,
      traceId,
      context: { online: navigator.onLine !== false },
    });
  }
  return traceId;
}

/** QueryCache `onError` handler. */
export function routeQueryError(error: unknown, query: Query<unknown, unknown>): void {
  const traceId = instrument(error, 'query');

  // The region that owns the request shows the failure inside itself (Q-506
  // card 1542): reported above, but no failure screen and no banner.
  if (query.meta?.[FAILURE_SHOWN_IN_META] === 'region') return;

  // Transport failures are handled by the connectivity layer's inline UI (#239).
  // No failure screen, no banner — the paused query + UnreachableState covers it.
  if (isTransportFailure(error)) return;

  // Presentation is still the 5xx class only — a 4xx or a schema throw
  // is the component's own inline concern (now with a trace id it can show).
  if (!isServerFailure(error)) return;

  // A query that has never had a successful fetch has no data for its
  // consumers to render — the screen is broken. One with prior data is still
  // showing valid (stale) content, so a banner is enough.
  if (query.state.dataUpdatedAt === 0) {
    useFailureStore.getState().show(buildFailure(error, traceId));
  } else {
    useFeedbackStore.getState().push(extractApiMessage(error, FALLBACK_MESSAGE), 'error');
  }
}

/**
 * The copy the central routing shows for a mutation failure it owns: the
 * `unreachable` copy for every transport failure, a timeout included (Q-609 a),
 * `extractApiMessage(error, FALLBACK_MESSAGE)` for a 5xx, and
 * null for anything else (4xx, validation, a non-HTTP throw: the component's
 * own concern). Shared with `presentCallerMutationError` (#1174-d), so a
 * caller-mode dialog shows exactly the copy the global banner showed (I4).
 */
export function centralFailureCopy(error: unknown): string | null {
  if (isTransportFailure(error)) {
    // Transport failures: the connectivity copy. A mutation has no loading
    // panel, so this push is the only surface (#239 D5). A request that got no
    // answer in 120 s reads the same as one that never reached Ving: "Can't
    // reach Ving" (owner Q-609 a, #1561-j).
    return formMessages.connectivity.unreachable;
  }
  if (isServerFailure(error)) {
    // A mutation is an explicit user action — the screen rendered fine before
    // they triggered it, so a failed mutation cannot have broken it.
    return extractApiMessage(error, FALLBACK_MESSAGE);
  }
  return null;
}

/**
 * MutationCache `onError` handler.
 *
 * TanStack calls it with `(error, variables, onMutateResult, mutation, …)`;
 * the `mutation` carries `meta.feedbackLayer` (stamped by `useMutation` from
 * `@/api/mutation`, #1174). A 5xx or transport failure shows ONCE, where the
 * request came from: in the band of the dialog whose child ran the action
 * while that opening of it is open, else on the global banner. A caller-mode
 * mutation (`CALLER_REPORTS`) gets nothing here: the dialog child awaiting it
 * presents it (`presentCallerMutationError`, #1174-d). `mutation` is
 * optional: unit tests call this with the error alone (→ global).
 */
export function routeMutationError(
  error: unknown,
  variables?: unknown,
  _onMutateResult?: unknown,
  mutation?: Mutation<unknown, unknown, unknown, unknown>,
): void {
  instrument(error, 'mutation', { reportKnownRefusal: mutation?.meta?.[REPORT_KNOWN_REFUSAL_META] === true });

  // The page that owns the action shows the failure itself (Q-506 card 1571):
  // still reported above, but no second copy on the global banner.
  if (mutation?.meta?.[PAGE_OWNS_FAILURE_META] === true) return;

  const copy = centralFailureCopy(error);
  if (copy === null) return;

  const target = mutation?.meta?.feedbackLayer;
  // The awaiting caller presents it (#1174-d: presentCallerMutationError).
  if (target === CALLER_REPORTS) return;
  if (target?.layer) {
    // The layer shows it in its band while that opening is open and mounted,
    // and forwards it to the global banner otherwise (#1174 I2/I3/I3a).
    // The action's key rides along so its next success can clear this (#1628).
    // A hook with `feedbackKeyOf` has one key per run (#1628-d4), from this run's variables.
    const layerSource = target.sourceOf ? target.sourceOf(variables as never) : target.source;
    target.layer.push(copy, 'error', layerSource === undefined ? target.opening : { opening: target.opening, source: layerSource });
    return;
  }
  const source = target?.sourceOf ? target.sourceOf(variables as never) : target?.source;
  useFeedbackStore.getState().push(copy, 'error', source === undefined ? undefined : { source });
}

/**
 * Wire the central error routing onto the app's shared QueryClient. main.tsx
 * calls this once at boot (#844b: extracted from main.tsx so the test-client
 * parity guard can run the exact production wiring without booting the app).
 */
export function wireAppQueryClient(): void {
  wireQueryErrorRouting(routeQueryError, routeMutationError);
}
